import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import { DebounceInput } from "react-debounce-input";
import { withRouter } from "react-router-dom";
import { withAlert } from "react-alert";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import { LIST_PAGE_SIZE } from "../../constants/listPagination";
import TeacherService from "../../services/TeacherService";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading";
import SearchableSelect from "../../waybee-ui/SearchableSelect";
import TeacherList from "./components/TeacherList";
import { AbilityContext } from "../../acl/can";

const LoadContainer = styled.div`
  text-align: center;
  margin: 8px;
`;

class Teachers extends Component {
  constructor() {
    super();
    this.loadSeq = 0;
    this.state = {
      teachers: [],
      classes: [],
      skip: 0,
      total: 0,
      loading: false,
      loadingClasses: false,
      errorClasses: false,
      errorTeachers: false,
      filter: {
        searchText: "",
        classId: ""
      }
    };
  }

  componentDidMount() {
    this.loadClasses();
    window.addEventListener("scroll", this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll = () => {
    const container = document.documentElement;
    if (
      container.scrollTop + window.innerHeight >=
      container.offsetHeight - 2
    ) {
      this.loadMoreTeachers();
    }
  };

  loadClasses = async () => {
    const { context } = this;
    this.setState({ loadingClasses: true, errorClasses: false });
    try {
      const classes = await TeacherService.getClasses();
      if (context.can("listAll", "diaryTeacher")) {
        this.setState({ classes }, () => this.loadTeachers(true));
      } else if (classes.length === 1) {
        this.setState(
          {
            classes,
            filter: {
              searchText: "",
              classId: classes[0].id
            }
          },
          () => this.loadTeachers(true)
        );
      } else {
        this.setState({ classes });
      }
    } catch (e) {
      const { alert, t } = this.props;
      this.setState({ classes: [], errorClasses: true });
      alert.show(t("classes.list.errorFetch"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ loadingClasses: false });
    }
  };

  loadTeachers = async (reset = false) => {
    const { filter, loading, skip } = this.state;
    const { context } = this;
    const canListAll = context.can("listAll", "diaryTeacher");
    if (!filter.classId && !canListAll) {
      this.loadSeq += 1;
      this.setState({
        teachers: [],
        skip: 0,
        total: 0,
        loading: false,
        errorTeachers: false
      });
      return;
    }
    if (loading && !reset) return;

    this.loadSeq += 1;
    const seq = this.loadSeq;
    const currentSkip = reset ? 0 : skip;

    this.setState(
      reset
        ? { loading: true, errorTeachers: false, teachers: [], skip: 0 }
        : { loading: true, errorTeachers: false }
    );

    try {
      const res = await TeacherService.getTeachers(
        filter.searchText,
        filter.classId,
        { limit: LIST_PAGE_SIZE, skip: currentSkip }
      );
      if (seq !== this.loadSeq) return;

      const items = (res && res.items) || (Array.isArray(res) ? res : []);
      const total =
        res && typeof res.total === "number"
          ? res.total
          : Number(res && res.total) || items.length;

      this.setState(prev => ({
        teachers: reset ? items : prev.teachers.concat(items),
        total,
        skip: currentSkip + items.length,
        loading: false
      }));
    } catch (e) {
      if (seq !== this.loadSeq) return;
      const { alert, t } = this.props;
      this.setState(prev => ({
        teachers: reset ? [] : prev.teachers,
        errorTeachers: true,
        loading: false
      }));
      alert.show(t("teachers.errorFetch"), {
        title: t("common.actions.error")
      });
    }
  };

  loadMoreTeachers = () => {
    const { teachers, total, loading, filter } = this.state;
    const { context } = this;
    const canListAll = context.can("listAll", "diaryTeacher");
    if (!filter.classId && !canListAll) return;
    if (loading || teachers.length >= total) return;
    this.loadTeachers(false);
  };

  setFilter = (field, value) => {
    const { context } = this;
    this.setState(
      prevState => ({
        filter: {
          ...prevState.filter,
          [field]: value
        },
        skip: 0,
        ...(field === "classId" && !value
          ? { teachers: [], total: 0, errorTeachers: false }
          : {})
      }),
      () => {
        const { filter: nextFilter } = this.state;
        if (nextFilter.classId || context.can("listAll", "diaryTeacher")) {
          this.loadTeachers(true);
        }
      }
    );
  };

  renderEmptyMessage = () => {
    const { context } = this;
    const { t } = this.props;
    const {
      loading,
      loadingClasses,
      teachers,
      classes,
      filter,
      errorClasses,
      errorTeachers
    } = this.state;
    const canListAll = context.can("listAll", "diaryTeacher");
    const hasClass = Boolean(filter.classId) || canListAll;
    let emptyTeachersMessage = t("teachers.empty.byClass");
    if (filter.searchText.trim()) {
      emptyTeachersMessage = t("teachers.empty.byName");
    } else if (canListAll && !filter.classId) {
      emptyTeachersMessage = t("teachers.empty.default");
    }

    if ((loading && teachers.length === 0) || loadingClasses) return null;

    if (errorClasses) {
      return (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">
            {t("teachers.errorLoadClasses")}
          </Typography>
        </Box>
      );
    }

    if (!classes.length) {
      return (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">
            {t("teachers.noClassesUser")}
          </Typography>
        </Box>
      );
    }

    if (!hasClass) {
      return (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">
            {t("teachers.selectClass")}
          </Typography>
        </Box>
      );
    }

    if (errorTeachers) {
      return (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">
            {t("teachers.errorLoadTeachers")}
          </Typography>
        </Box>
      );
    }

    if (!teachers.length) {
      return (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">{emptyTeachersMessage}</Typography>
        </Box>
      );
    }

    return null;
  };

  render() {
    const { loading, loadingClasses, teachers, filter, classes } = this.state;
    const { history, t } = this.props;
    const { context } = this;
    const canListAll = context.can("listAll", "diaryTeacher");
    const hasClass = Boolean(filter.classId) || canListAll;
    const emptyMessage = this.renderEmptyMessage();

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Group>
            <Box>
              <Heading level={1} icon="search" gutterBottom>
                {t("teachers.searchTitle")}
              </Heading>

              <Box pb={4} pt={2}>
                <form noValidate autoComplete="off">
                  <Grid container spacing={8}>
                    <Grid item md={6}>
                      <SearchableSelect
                        label={t("common.labels.class")}
                        options={classes}
                        value={
                          classes.find(c => c.id === filter.classId) || null
                        }
                        disabled={loadingClasses || !classes.length}
                        disableClearable={!canListAll}
                        onChange={classe =>
                          this.setFilter("classId", classe ? classe.id : "")}
                        getOptionLabel={o => o.name || o.id}
                        getOptionSelected={(a, b) => a.id === b.id}
                        TextFieldProps={{ required: !canListAll }}
                        noOptionsText={t("common.messages.noClass")}
                      />
                    </Grid>

                    <Grid item md={6}>
                      <DebounceInput
                        minLength={0}
                        debounceTimeout={400}
                        element={TextField}
                        value={filter.searchText}
                        disabled={!hasClass}
                        onChange={e =>
                          this.setFilter("searchText", e.target.value)}
                        label={t("teachers.searchByName")}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </form>
              </Box>

              {emptyMessage}

              {hasClass && teachers.length > 0 && (
                <TeacherList
                  onSelectItem={teacher => {
                    history.push(`/teachers/${teacher.id}`);
                  }}
                  teachers={teachers}
                />
              )}

              {loading && teachers.length === 0 && (
                <Box p={6} textAlign="center">
                  <CircularProgress />
                </Box>
              )}
              {loading && teachers.length > 0 && (
                <LoadContainer>
                  <CircularProgress size={24} />
                </LoadContainer>
              )}
            </Box>
          </Group>
        </Grid>
      </Grid>
    );
  }
}

Teachers.contextType = AbilityContext;

Teachers.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  history: PropTypes.object.isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withRouter(withTranslation()(Teachers)));
